import { inject, Injectable, OnDestroy } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import * as signalR from '@microsoft/signalr';
import { getCurrentAuthenticatedUser } from '@tmx/auth';
import { subscribeOnce } from '@tmx/core';
import { accountsEnvironment } from '@tmx/data-access';
import { ToastService } from '@tmx/modus-operandi';
import {
  BehaviorSubject,
  filter,
  first,
  from,
  map,
  race,
  Subject,
  Subscription,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

import { SelectAllBulkEvent } from '../models/select-all-bulk-event/select-all-bulk-event';
import {
  isViewingChildAccount$,
  userAuthenticated$,
  userLoggedOut$,
} from '../repositories/account-manager/account-manager.repository';
import { assetDeleteOperationEventSchema } from '../schema/asset-delete-operation-event.schema';
import { AssetDeleteResult } from '../schema/asset-delete-result.schema';
import { assetInviteCompletedEventSchema } from '../schema/asset-invite-completed-event.schema';
import { assetInviteErrorEventSchema } from '../schema/asset-invite-error-event.schema';
import { AssetInviteResult } from '../schema/asset-invite-result.schema';
import { assetLicenseAssignmentsEventSchema } from '../schema/asset-license-assignments-event.schema';
import { AssetLicenseAssignmentsResult } from '../schema/asset-license-assignments-result.schema';
import { assetPasswordResetOperationEventSchema } from '../schema/asset-password-reset-operation-event.schema';
import { AssetPasswordResetResult } from '../schema/asset-password-reset-result.schema';
import { notifyAssetErrorEventSchema } from '../schema/notify-asset-error-event.schema';
import { notifyAssetOperationEventSchema } from '../schema/notify-asset-operation-event.schema';
import { NotifyAssetResult } from '../schema/notify-asset-result.schema';
import { reportToTrackjs } from '../utils/report-to-trackjs';

const WAIT_FOR_RESPONSE_MS = 10_000;
const DELAY_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class SelectAllBulkActionService implements OnDestroy {
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);

  private connection: null | signalR.HubConnection = null;
  private connectionSubscription: null | Subscription = null;

  private readonly events = new BehaviorSubject<SelectAllBulkEvent<unknown>[]>(
    []
  );
  private waitingIds: string[] = [];

  private readonly cancel$ = new Subject<void>();

  constructor() {
    subscribeOnce(userAuthenticated$, () => {
      this.startSignalR();
    });

    // No unsubscribe because we want to listen for entire app lifetime.
    userLoggedOut$.subscribe(() => {
      this.stopSignalR();
    });

    // No unsubscribe because we want to listen for entire app lifetime.
    isViewingChildAccount$.subscribe(() => {
      this.restartSignalR();
    });
  }

  onEvent(id: string) {
    const eventStream$ = this.events.pipe(
      map((events) =>
        events.find((event) => !event.handled && event.id === id)
      ),
      filter((specificEvent) => specificEvent !== undefined),
      tap((specificEvent) => {
        specificEvent.handled = true;
      }),
      first()
    );

    const timeout$ = timer(WAIT_FOR_RESPONSE_MS).pipe(
      takeUntil(this.cancel$),
      tap(() => {
        if (this.connection?.state !== signalR.HubConnectionState.Connected) {
          return;
        }

        this.waitingIds.push(id);
        this.toastService.add({
          type: 'primary',
          title: this.translocoService.translate(
            'assets.selectAllBulkAction.bulkActionInProgress'
          ),
          content: this.translocoService.translate(
            'assets.selectAllBulkAction.bulkActionInProgressContent'
          ),
          delay: DELAY_MS,
        });
      })
    );

    return race(eventStream$, timeout$);
  }

  cancelWaitForResponse() {
    this.cancel$.next();
  }

  ngOnDestroy() {
    this.stopSignalR();
  }

  private startSignalR() {
    this.stopSignalR();

    const authToken = getCurrentAuthenticatedUser().authToken;
    const url = `${accountsEnvironment.baseUrl}/api/v1/messages?authToken=${authToken}`;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .configureLogging(signalR.LogLevel.None)
      .build();

    this.connection.on(
      'AssetLicenseAssignments',
      (event: Record<string, unknown>) => {
        this.handleAssetLicenseAssignments(event);
      }
    );
    this.connection.on(
      'AssetLicencseAssignments',
      (event: Record<string, unknown>) => {
        this.handleAssetLicenseAssignments(event);
      }
    );

    this.connection.on(
      'NotifyAssetOperation',
      (event: Record<string, unknown>) => {
        this.handleNotifyAssetOperation(event);
      }
    );
    this.connection.on('NotifyAssetError', (event: Record<string, unknown>) => {
      this.handleNotifyAssetError(event);
    });

    this.connection.on(
      'AssetInviteCompleted',
      (event: Record<string, unknown>) => {
        this.handleAssetInviteCompleted(event);
      }
    );
    this.connection.on('AssetInviteError', (event: Record<string, unknown>) => {
      this.handleAssetInviteError(event);
    });

    this.connection.on(
      'AssetPasswordResetOperation',
      (event: Record<string, unknown>) => {
        this.handleAssetPasswordResetOperation(event);
      }
    );

    this.connection.on(
      'AssetDeleteOperation',
      (event: Record<string, unknown>) => {
        this.handleAssetDeleteOperation(event);
      }
    );

    this.connectionSubscription = from(this.connection.start()).subscribe({
      error: () => {
        reportToTrackjs(
          'notification repository',
          'SignalR could not connect to hub.'
        );
      },
    });
  }

  private stopSignalR() {
    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
      this.connectionSubscription = null;
    }

    if (this.connection) {
      this.connection.off('AssetLicenseAssignments');
      this.connection.off('AssetLicencseAssignments');
      this.connection.off('NotifyAssetOperation');
      this.connection.off('NotifyAssetError');
      this.connection.off('AssetInviteCompleted');
      this.connection.off('AssetInviteError');
      this.connection.off('AssetPasswordResetOperation');
      this.connection.off('AssetDeleteOperation');
      this.connection.stop();
      this.connection = null;
    }
  }

  private restartSignalR() {
    this.stopSignalR();
    this.startSignalR();
  }

  private addEvent(event: SelectAllBulkEvent<unknown>) {
    this.events.next([
      ...this.events.value.filter(({ handled }) => !handled),
      event,
    ]);
  }

  private handleAssetLicenseAssignments(event: Record<string, unknown>) {
    const { success, data } =
      assetLicenseAssignmentsEventSchema.safeParse(event);
    if (!success || data.type !== 'result') {
      return;
    }

    const { id, eventType, result } = data;
    const unassign = eventType === 'AssetLicenseUnassigned';
    const error = result.errorCode !== null;
    if (this.waitingIds.includes(id)) {
      this.toastService.add({
        type: error ? 'danger' : 'success',
        title: this.translocoService.translate(
          unassign
            ? this.translocoService.translate(
                'assets.selectAllBulkAction.unassignAllLicensesTitle'
              )
            : this.translocoService.translate(
                'assets.selectAllBulkAction.assignAllLicensesTitle'
              )
        ),
        content: error
          ? this.translocoService.translate(
              'assets.selectAllBulkAction.bulkActionErrorContent'
            )
          : this.translocoService.translate(
              'assets.selectAllBulkAction.bulkActionCompletedContent'
            ),
        delay: DELAY_MS,
      });

      this.waitingIds = this.waitingIds.filter((waitingId) => waitingId !== id);
      return;
    }

    this.addEvent(
      new SelectAllBulkEvent<AssetLicenseAssignmentsResult>(
        id,
        unassign ? 'unassign' : 'assign',
        result
      )
    );
  }

  private handleNotifyAssetOperation(event: Record<string, unknown>) {
    const { success, data } = notifyAssetOperationEventSchema.safeParse(event);
    if (!success || data.type !== 'result') {
      return;
    }

    const { id, result } = data;
    const error = result.errorCode !== null;
    if (this.waitingIds.includes(id)) {
      this.toastService.add({
        type: error ? 'danger' : 'success',
        title: this.translocoService.translate(
          'assets.selectAllBulkAction.notifyAllUsersTitle'
        ),
        content: this.translocoService.translate(
          error
            ? this.translocoService.translate(
                'assets.selectAllBulkAction.notifyError'
              )
            : this.translocoService.translate(
                'assets.selectAllBulkAction.notifySuccess'
              )
        ),
        delay: DELAY_MS,
      });

      this.waitingIds = this.waitingIds.filter((waitingId) => waitingId !== id);
      return;
    }

    this.addEvent(
      new SelectAllBulkEvent<NotifyAssetResult>(id, 'notify', result)
    );
  }

  private handleNotifyAssetError(event: Record<string, unknown>) {
    const { success, data } = notifyAssetErrorEventSchema.safeParse(event);
    if (!success || data.type !== 'result') {
      return;
    }

    const { id, result } = data;
    const error = result.errorCode !== null;
    if (this.waitingIds.includes(id)) {
      this.toastService.add({
        type: error ? 'danger' : 'success',
        title: this.translocoService.translate(
          'assets.selectAllBulkAction.notifyAllUsersTitle'
        ),
        content: this.translocoService.translate(
          error
            ? this.translocoService.translate(
                'assets.selectAllBulkAction.notifyError'
              )
            : this.translocoService.translate(
                'assets.selectAllBulkAction.notifySuccess'
              )
        ),
        delay: DELAY_MS,
      });

      this.waitingIds = this.waitingIds.filter((waitingId) => waitingId !== id);
      return;
    }

    this.addEvent(
      new SelectAllBulkEvent<NotifyAssetResult>(id, 'notify', result)
    );
  }

  private handleAssetInviteWaiting({
    errorCode,
    totalInvitesSent,
    totalInvitesFailed,
  }: AssetInviteResult) {
    if (errorCode === null) {
      if (totalInvitesSent > 0) {
        this.toastService.add({
          type: 'success',
          title: this.translocoService.translate(
            'assets.selectAllBulkAction.inviteTitle'
          ),
          content: this.translocoService.translate(
            'assets.selectAllBulkAction.selectAllInviteSuccess',
            {
              count: totalInvitesSent,
            }
          ),
          delay: DELAY_MS,
        });
      }

      if (totalInvitesFailed > 0) {
        this.toastService.add({
          type: 'danger',
          title: this.translocoService.translate(
            'assets.selectAllBulkAction.inviteTitle'
          ),
          content: this.translocoService.translate(
            'assets.selectAllBulkAction.selectAllInviteFail',
            {
              count: totalInvitesFailed,
            }
          ),
          delay: DELAY_MS,
        });
      }

      if (totalInvitesSent === 0 && totalInvitesFailed === 0) {
        this.toastService.add({
          type: 'success',
          title: this.translocoService.translate(
            'assets.selectAllBulkAction.inviteTitle'
          ),
          content: this.translocoService.translate(
            'assets.selectAllBulkAction.inviteAllSuccess'
          ),
          delay: DELAY_MS,
        });
      }
    } else {
      this.toastService.add({
        type: 'danger',
        title: this.translocoService.translate(
          'assets.selectAllBulkAction.inviteTitle'
        ),
        content: this.translocoService.translate(
          'assets.selectAllBulkAction.inviteFailedDefault'
        ),
        delay: DELAY_MS,
      });
    }
  }

  private handleAssetInviteCompleted(event: Record<string, unknown>) {
    const { success, data } = assetInviteCompletedEventSchema.safeParse(event);
    if (!success || data.type !== 'result') {
      return;
    }

    const { id, result } = data;
    if (this.waitingIds.includes(id)) {
      this.handleAssetInviteWaiting(result);
      this.waitingIds = this.waitingIds.filter((waitingId) => waitingId !== id);
      return;
    }

    this.addEvent(
      new SelectAllBulkEvent<AssetInviteResult>(id, 'invite', result)
    );
  }

  private handleAssetInviteError(event: Record<string, unknown>) {
    const { success, data } = assetInviteErrorEventSchema.safeParse(event);
    if (!success || data.type !== 'result') {
      return;
    }

    const { id, result } = data;
    if (this.waitingIds.includes(id)) {
      this.handleAssetInviteWaiting(result);
      this.waitingIds = this.waitingIds.filter((waitingId) => waitingId !== id);
      return;
    }

    this.addEvent(
      new SelectAllBulkEvent<AssetInviteResult>(id, 'invite', result)
    );
  }

  private handleAssetPasswordResetOperation(event: Record<string, unknown>) {
    const { success, data } =
      assetPasswordResetOperationEventSchema.safeParse(event);
    if (!success || data.type !== 'result') {
      return;
    }

    const { id, result } = data;
    const error = result.errorCode !== null;
    if (this.waitingIds.includes(id)) {
      this.toastService.add({
        type: error ? 'danger' : 'success',
        title: this.translocoService.translate(
          'assets.selectAllBulkAction.passwordResetTitle'
        ),
        content: this.translocoService.translate(
          error
            ? this.translocoService.translate(
                'assets.selectAllBulkAction.passwordResetFailedDefault'
              )
            : this.translocoService.translate(
                'assets.selectAllBulkAction.passwordResetSuccessCount',
                { count: result.totalPasswordResets }
              )
        ),
        delay: DELAY_MS,
      });

      this.waitingIds = this.waitingIds.filter((waitingId) => waitingId !== id);
      return;
    }

    this.addEvent(
      new SelectAllBulkEvent<AssetPasswordResetResult>(
        id,
        'reset-password',
        result
      )
    );
  }

  private handleAssetDeleteOperation(event: Record<string, unknown>) {
    const { success, data } = assetDeleteOperationEventSchema.safeParse(event);
    if (!success || data.type !== 'result') {
      return;
    }

    const { id, result } = data;
    const error = result.errorCode !== null;
    if (this.waitingIds.includes(id)) {
      this.toastService.add({
        type: error ? 'danger' : 'success',
        title: this.translocoService.translate(
          'assets.selectAllBulkAction.deleteAllTitle'
        ),
        content: this.translocoService.translate(
          error
            ? this.translocoService.translate(
                'assets.selectAllBulkAction.deleteAllFailed'
              )
            : this.translocoService.translate(
                'assets.selectAllBulkAction.deleteAllSuccess'
              )
        ),
        delay: DELAY_MS,
      });

      this.waitingIds = this.waitingIds.filter((waitingId) => waitingId !== id);
      return;
    }

    this.addEvent(
      new SelectAllBulkEvent<AssetDeleteResult>(id, 'delete', result)
    );
  }
}
